import { Bullet, Circle, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import BarChart from "@/classes/BarChart";
import { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class extends BarChart {
  public loadChart(chartName: string, family: string): void {
    super.loadChart(chartName, family);
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;
  }

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.yAxis) as time.ITimeInterval;
    }

    this.createChart({ noTopPadding: true });
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = this.isDateAxis ? [this.settings.xAxisPrimary] : this.data;

    const lineColorIndex = 1;

    series.reverse().forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = (index % paletteLength >= lineColorIndex ? index + 1 : index) % paletteLength;
      this.createBarSeries(el, indexColor);
    });
    this.createLineSeries(this.settings.xAxisSecondary, lineColorIndex);

    const legendNames: string[] = this.isDateAxis
      ? [this.settings.xAxisPrimary, this.settings.xAxisSecondary]
      : (this.data.map((item) => item[this.settings.yAxis]) as string[]);

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData, false, false);
  }

  public createBarSeries(item: ChartDataRecord, index: number) {
    const seriesConfig = {
      name: this.isDateAxis ? this.settings.xAxisPrimary : (item[this.settings.yAxis] as string),
      xAxis: this.axes.xPrimary,
      yAxis: this.axes.y,
      valueXField: this.settings.xAxisPrimary,
      [this.isDateAxis ? "valueYField" : "categoryYField"]: this.settings.yAxis,
      fill: this.getSeriesColor(index),
    };

    const series: ColumnSeries = (this.chart as XYChart).series.push(ColumnSeries.new(this.root, seriesConfig));

    if (!this.isDateAxis) {
      series.set("clustered", false);
    }

    if (this.settings.showBarDataLabels) {
      const metricFormatter = this.formatters?.[this.settings.xAxisPrimary];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      const axis = series.get("xAxis") as ValueAxis<AxisRenderer>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerY: p50,
          dx: 8,
          fontSize: labelFontSize,
          layer: 1000,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueX");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig as any);
        });

        label.adapters.add("visible", () => {
          const v = dataItem?.get("valueX");
          return typeof v === "number" ? v > 0 : !!v;
        });

        return Bullet.new(this.root, {
          locationX: 1,
          sprite: label,
        });
      });
    }

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueXField") === key || series.get("categoryYField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    if (this.isDateAxis) {
      this.createLegendLabel(series, this.settings.xAxisPrimary);
      this.updateBarSeriesData(this.settings.xAxisPrimary, series);
    } else {
      series.data.push(item);
    }
  }

  createRenderers() {
    const y = AxisRendererY.new(this.root, {});
    const xPrimary = AxisRendererX.new(this.root, {});
    const xSecondary = AxisRendererX.new(this.root, {
      opposite: true,
    });

    y.labels.template.setAll({ ...chartFontSettings.label });
    xPrimary.labels.template.setAll({ ...chartFontSettings.label });
    xSecondary.labels.template.setAll({ ...chartFontSettings.label });

    this.renderers = { y, xPrimary, xSecondary };
  }

  createAxes() {
    const y = this.isDateAxis
      ? (this.chart as XYChart).yAxes.push(
          DateAxis.new(this.root, {
            renderer: this.renderers.y as AxisRendererY,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.yAxis),
            marginRight: 14,
          })
        )
      : (this.chart as XYChart).yAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.yAxis,
            renderer: this.renderers.y as AxisRendererY,
            marginRight: 14,
          })
        );

    if (this.settings.yAxisTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    y.data.setAll(this.data);

    if (
      this.module === PLAN_MODULE &&
      this.isDateAxis &&
      this.settings.yAxis === "week" &&
      this.settings.showDataLabels
    ) {
      const yAxis = y as DateAxis<AxisRendererY>;
      this.root.events.once("frameended", () => {
        const privateMin = yAxis.getPrivate("min");
        const privateMax = yAxis.getPrivate("max");
        const privateStep = yAxis.getPrivate("step");
        if (
          typeof privateMin === "number" &&
          typeof privateMax === "number" &&
          typeof privateStep === "number" &&
          privateStep > 0
        ) {
          yAxis.set("min", privateMin - privateStep);
          yAxis.set("max", privateMax + privateStep);
          yAxis.set("strictMinMax", true);
        }
      });
    }

    const xPrimary = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.xPrimary as AxisRenderer,
        min: 0,
        marginTop: 14,
        numberFormat: this.getMetricFormatter(this.settings.xAxisPrimary, { notation: "compact" }),
      })
    );

    if (this.settings.xAxisPrimaryTitle) {
      xPrimary.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    const xSecondary = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.xSecondary as AxisRenderer,
        min: 0,
        marginTop: 0,
        numberFormat: this.getMetricFormatter(this.settings.xAxisSecondary, { notation: "compact" }),
      })
    );

    if (this.settings.xAxisSecondaryTitle) {
      xSecondary.children.unshift(
        Label.new(this.root, {
          text: this.settings.xAxisSecondaryTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: 0,
          centerX: p50,
        })
      );
    }

    this.axes = { xPrimary, xSecondary, y };
  }

  protected createLineSeries(seriesName: string, index: number) {
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series: LineSeries = (this.chart as XYChart).series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.xSecondary,
        yAxis: this.axes.y,
        fill: this.getSeriesColor(index),
        minBulletDistance: 10,
        [this.isDateAxis ? "valueYField" : "categoryYField"]: this.settings.yAxis,
        valueXField: seriesName,
      })
    );

    if (this.options.showBullets) {
      series.bullets.push(() => {
        return Bullet.new(this.root, {
          sprite: Circle.new(this.root, {
            radius: 4,
            fill: series.get("fill"),
          }),
        });
      });
    }

    if (this.settings.showLineDataLabels) {
      const metricFormatter = this.formatters?.[seriesName];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsLinesSizes ?? DEFAULT_DATA_LABEL_SIZE);

      const axis = series.get("xAxis") as ValueAxis<AxisRenderer>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerY: p50,
          dx: 8,
          fontSize: labelFontSize,
          layer: 1000,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueX");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig as any);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueX");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          sprite: label,
        });
      });
    }

    this.createTooltip(series, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueXField") === key || series.get("categoryYField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    series.set("stroke", this.getSeriesColor(index));
    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    this.createLegendLabel(series, seriesName);
    this.updateLineSeriesData(seriesName, series);
  }

  protected updateLineSeriesData(seriesName: string, series: LineSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.yAxis]: stringOrNumberToDate(el[this.settings.yAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data.reverse();

    data.sort((a, b) => {
      return (a[this.settings.yAxis] as number) - (b[this.settings.yAxis] as number);
    });

    series.data.setAll(data);
  }

  mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (this.module === PLAN_MODULE && this.settings?.showItems && Number.isFinite(this.settings?.showItems)) {
      const decompositionMetric = this.settings.yAxis;
      const displayMetric = this.settings.xAxisPrimary;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((item) => ({
      [this.settings.yAxis]: item[this.settings.yAxis] ?? 0,
      [this.settings.xAxisPrimary]: item[this.settings.xAxisPrimary] ?? 0,
      [this.settings.xAxisSecondary]: item[this.settings.xAxisSecondary] ?? 0,
    }));

    if (!this.isDateAxis) {
      preparedData = orderBy(preparedData, [this.settings.xAxisPrimary], ["asc"]) as ChartDataRecord[];
      preparedData = this.showItems(
        preparedData as ChartDataRecord[],
        this.settings.yAxis,
        this.settings.xAxisPrimary,
        "first"
      );
    }

    return preparedData;
  }
}
